import React, { useEffect, useState } from 'react'
import { Card, CardHeader } from '../../components/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/table'
import { Input } from '../../components/input'
import { Button } from '../../components/button'
import { Badge } from '../../components/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../../components/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/alert-dialog'
import {
  Search,
  Plus,
  Upload,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  Image as ImageIcon,
  X
} from 'lucide-react'
import { toast } from 'sonner'
import MediaUploader from '../MediaUploader'
import { normalizeUrl } from '../../lib/UrlNormalize'

export interface CatalogVariantItem {
  stock_id: number
  sku: string
  variant: string
  title: string
  type_of_jewelry: string
  setting_style?: string
  diamond_count?: number
  dimensions?: string
  item_weight?: string
  other?: string
  metal_type?: string
  diameter?: string
  carat_weight?: number | string
  diamond_quality?: string
  price?: number | string
  media_attachment?: string
  is_active: number
  created_at?: string
  updated_at?: string
}

export interface CatalogProductItem {
  sku: string
  title: string
  type_of_jewelry: string
  setting_style?: string
  variantCount: number
  stoneOptionCount: number
  metalCount: number
  minPrice: number | null
  maxPrice: number | null
  activeCount: number
  media_attachment?: string | null
  variants: CatalogVariantItem[]
}

interface CatalogManagementTableProps {
  items: CatalogProductItem[]
  totalItems: number
  currentPage: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  isLoading: boolean
  onRefresh: () => void
  search: string
  onSearchChange: (search: string) => void
  typeFilter: string
  onTypeFilterChange: (type: string) => void
  metalFilter: string
  onMetalFilterChange: (metal: string) => void
  statusFilter: string
  onStatusFilterChange: (status: string) => void
  typeOptions: string[]
  metalOptions: string[]
  onUpdateVariant: (
    stockId: number,
    data: Partial<CatalogVariantItem>
  ) => Promise<void>
  onDeleteVariant: (stockId: number, hard?: boolean) => Promise<void>
  onOpenAddProduct: () => void
  onOpenSyncModal: () => void
  onUploadMedia: (files: File[]) => Promise<string[]>
}

const currency = (val: number | string) =>
  `$${Number(val).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`

interface EditableCellProps {
  value: string | number | null | undefined
  onSave: (val: string) => Promise<void>
  type?: 'text' | 'number'
  format?: 'currency' | 'standard'
}

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onSave,
  type = 'text',
  format = 'standard'
}) => {
  const incoming = String(value ?? '')
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(incoming)
  const [isSaving, setIsSaving] = useState(false)

  // The server normalises what it stores (money strings become decimals, junk
  // becomes NULL). Without this the cell keeps showing what was typed rather
  // than what was saved.
  useEffect(() => {
    if (!isEditing) setDraft(incoming)
  }, [incoming, isEditing])

  const submit = async () => {
    if (draft === incoming) {
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await onSave(draft)
      setIsEditing(false)
    } catch {
      // stay in edit mode so the value isn't silently lost
    } finally {
      setIsSaving(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          } else if (e.key === 'Escape') {
            setDraft(incoming)
            setIsEditing(false)
          }
        }}
        onBlur={submit}
        disabled={isSaving}
        className="h-8 w-full min-w-[5rem] rounded-lg border-border bg-background text-body2 shadow-none"
      />
    )
  }

  const display =
    format === 'currency' && incoming !== '' && !isNaN(Number(incoming))
      ? currency(incoming)
      : incoming || '—'

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      title="Click to edit"
      className="-mx-2 rounded-lg px-2 py-1 text-left text-body2 transition-colors hover:bg-muted/60"
    >
      {display}
    </button>
  )
}

const parseMediaUrls = (mediaAttachment?: string | null): string[] => {
  if (!mediaAttachment) return []
  try {
    const parsed = JSON.parse(mediaAttachment)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// Read-only preview, or an editor when onSave/onUpload are passed.
const MediaCell: React.FC<{
  urls: string[]
  onSave?: (urls: string[]) => Promise<void>
  onUpload?: (files: File[]) => Promise<string[]>
}> = ({ urls, onSave, onUpload }) => {
  if (onSave && onUpload) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 gap-1.5">
            <ImageIcon className="h-4 w-4" />
            {urls.length || 'Add'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 space-y-2 p-3">
          <p className="text-body2 font-medium">Media ({urls.length})</p>
          <MediaUploader
            value={urls}
            handleSendToUpload={async (files) => {
              const uploaded = await onUpload(files)
              if (uploaded.length) await onSave([...urls, ...uploaded])
            }}
            onRemove={(index) => onSave(urls.filter((_, i) => i !== index))}
          />
        </PopoverContent>
      </Popover>
    )
  }

  if (urls.length === 0) return <span className="text-muted-foreground">—</span>

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5">
          <ImageIcon className="h-4 w-4" />
          {urls.length}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2 p-3">
        <p className="text-body2 font-medium">Attached media ({urls.length})</p>
        <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto">
          {urls.map((url, i) => (
            <div
              key={i}
              className="relative aspect-square overflow-hidden rounded-md border border-border bg-muted"
            >
              {url.endsWith('.mp4') ? (
                <video src={normalizeUrl(url)} className="h-full w-full object-cover" />
              ) : (
                <img
                  src={normalizeUrl(url)}
                  alt={`Media ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export const CatalogManagementTable: React.FC<CatalogManagementTableProps> = ({
  items,
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
  onRefresh,
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  metalFilter,
  onMetalFilterChange,
  statusFilter,
  onStatusFilterChange,
  typeOptions,
  metalOptions,
  onUpdateVariant,
  onDeleteVariant,
  onOpenAddProduct,
  onOpenSyncModal,
  onUploadMedia
}) => {
  const [expandedSkus, setExpandedSkus] = useState<Set<string>>(new Set())
  const [pendingDelete, setPendingDelete] = useState<CatalogVariantItem | null>(
    null
  )

  const toggleExpand = (sku: string) =>
    setExpandedSkus((prev) => {
      const next = new Set(prev)
      if (next.has(sku)) next.delete(sku)
      else next.add(sku)
      return next
    })

  const handleExportCSV = () => {
    const rows = items.flatMap((p) => p.variants)
    if (rows.length === 0) {
      toast.error('No items to export')
      return
    }

    const headers = [
      'Parent SKU',
      'SKU',
      'Title',
      'Type of Jewelry',
      'Setting Style',
      'Diamond Count',
      'Dimensions',
      'Item Weight',
      'Other',
      'Metal Type',
      'Diameter',
      'Carat Weight',
      'Diamond Quality',
      'Price',
      'Status'
    ]

    const quote = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const body = rows.map((i) =>
      [
        i.sku,
        i.variant,
        i.title,
        i.type_of_jewelry,
        i.setting_style,
        i.diamond_count,
        i.dimensions,
        i.item_weight,
        i.other,
        i.metal_type,
        i.diameter,
        i.carat_weight,
        i.diamond_quality,
        i.price,
        i.is_active ? 'Active' : 'Inactive'
      ].map(quote)
    )

    // Blob rather than a data: URI -- encodeURI leaves '#' intact, so a title
    // containing one truncates the whole file at that row.
    const blob = new Blob(
      [[headers.map(quote).join(','), ...body.map((r) => r.join(','))].join('\n')],
      { type: 'text/csv;charset=utf-8;' }
    )
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `catalog_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
    toast.success(`Exported ${rows.length} variants from this page`)
  }

  const totalPages = Math.ceil(totalItems / pageSize) || 1

  const update = async (
    v: CatalogVariantItem,
    data: Partial<CatalogVariantItem>,
    message: string
  ) => {
    await onUpdateVariant(v.stock_id, data)
    toast.success(message)
  }

  return (
    <Card className="m-4 gap-0 rounded-md border border-border py-0 md:m-[24px]">
      <CardHeader className="flex flex-col gap-3 border-b border-border px-4 py-3.5 md:px-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="font-semibold text-heading5">Inventory</h1>
          <p className="text-body2 text-muted-foreground">
            {totalItems} products
          </p>
        </div>

        <div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search SKU, title or metal"
              className="h-10 rounded-lg border-border bg-muted/40 pl-10 pr-9 text-body2 shadow-none transition-colors focus-visible:bg-background"
            />
            {search ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => onSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <Select value={typeFilter} onValueChange={onTypeFilterChange}>
            <SelectTrigger className="h-10 w-full rounded-lg border-border bg-muted/40 text-body2 shadow-none sm:w-40">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeOptions.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={metalFilter} onValueChange={onMetalFilterChange}>
            <SelectTrigger className="h-10 w-full rounded-lg border-border bg-muted/40 text-body2 shadow-none sm:w-40">
              <SelectValue placeholder="All metals" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All metals</SelectItem>
              {metalOptions.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="h-10 w-full rounded-lg border-border bg-muted/40 text-body2 shadow-none sm:w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2.5">
            <Button
              variant="outline"
              size="icon"
              className="size-10"
              aria-label="Refresh inventory"
              onClick={onRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={isLoading ? 'animate-spin' : ''} />
            </Button>
            <Button variant="outline" className="h-10" onClick={handleExportCSV}>
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" className="h-10" onClick={onOpenSyncModal}>
              <Upload className="h-4 w-4" />
              Import sheet
            </Button>
            <Button className="h-10" onClick={onOpenAddProduct}>
              <Plus className="h-4 w-4" />
              Add product
            </Button>
          </div>
        </div>
      </CardHeader>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="px-4 py-3">Parent SKU</TableHead>
              <TableHead className="px-4 py-3 min-w-[14rem]">Title</TableHead>
              <TableHead className="px-4 py-3">Type</TableHead>
              <TableHead className="px-4 py-3">Matrix</TableHead>
              <TableHead className="px-4 py-3">Variants</TableHead>
              <TableHead className="px-4 py-3">Price range</TableHead>
              <TableHead className="px-4 py-3 text-center">Media</TableHead>
              <TableHead className="px-4 py-3 text-center">Status</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="h-24 text-center text-muted-foreground"
                >
                  No products match your filters.
                </TableCell>
              </TableRow>
            ) : (
              items.map((product) => {
                const isExpanded = expandedSkus.has(product.sku)
                const allActive = product.activeCount === product.variantCount

                return (
                  <React.Fragment key={product.sku}>
                    <TableRow
                      onClick={() => toggleExpand(product.sku)}
                      className="cursor-pointer"
                    >
                      <TableCell className="w-10 px-4 py-3 text-muted-foreground">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 font-medium">
                        {product.sku}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {product.title || '—'}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {product.type_of_jewelry ? (
                          <Badge variant="outline">{product.type_of_jewelry}</Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {product.stoneOptionCount} stone
                        {product.stoneOptionCount === 1 ? '' : 's'} ×{' '}
                        {product.metalCount} metal
                        {product.metalCount === 1 ? '' : 's'}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {product.variantCount}
                      </TableCell>
                      <TableCell className="px-4 py-3 whitespace-nowrap font-medium">
                        {product.minPrice === null
                          ? '—'
                          : product.minPrice === product.maxPrice
                            ? currency(product.minPrice)
                            : `${currency(product.minPrice)} – ${currency(product.maxPrice!)}`}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        <MediaCell urls={parseMediaUrls(product.media_attachment)} />
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        <Badge variant={product.activeCount > 0 ? 'outline' : 'outline'}>
                          {allActive
                            ? 'Active'
                            : product.activeCount === 0
                              ? 'Inactive'
                              : `${product.activeCount}/${product.variantCount} active`}
                        </Badge>
                      </TableCell>
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={9} className="bg-muted/30 p-0">
                          <div className="overflow-x-auto p-3">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="px-3 py-2">Variant</TableHead>
                                  <TableHead className="px-3 py-2">Metal</TableHead>
                                  <TableHead className="px-3 py-2">Carat</TableHead>
                                  <TableHead className="px-3 py-2">Quality</TableHead>
                                  <TableHead className="px-3 py-2">Price</TableHead>
                                  <TableHead className="px-3 py-2 text-center">
                                    Media
                                  </TableHead>
                                  <TableHead className="px-3 py-2 text-center">
                                    Status
                                  </TableHead>
                                  <TableHead className="px-3 py-2 text-center">
                                    Actions
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {product.variants.map((v) => (
                                  <TableRow
                                    key={v.stock_id}
                                    className={v.is_active ? '' : 'opacity-60'}
                                  >
                                    <TableCell className="px-3 py-2 font-medium whitespace-nowrap">
                                      {v.variant}
                                    </TableCell>
                                    <TableCell className="px-3 py-2 whitespace-nowrap">
                                      {v.metal_type || '—'}
                                    </TableCell>
                                    <TableCell className="px-3 py-2">
                                      <EditableCell
                                        value={v.carat_weight}
                                        type="number"
                                        onSave={(carat_weight) =>
                                          update(v, { carat_weight }, 'Carat updated')
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                                      {v.diamond_quality || '—'}
                                    </TableCell>
                                    <TableCell className="px-3 py-2 font-medium">
                                      <EditableCell
                                        value={v.price}
                                        type="number"
                                        format="currency"
                                        onSave={(price) =>
                                          update(v, { price }, 'Price updated')
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className="px-3 py-2 text-center">
                                      <MediaCell
                                        urls={parseMediaUrls(v.media_attachment)}
                                        onUpload={onUploadMedia}
                                        onSave={(urls) =>
                                          update(
                                            v,
                                            { media_attachment: JSON.stringify(urls) },
                                            `Media updated for all ${v.metal_type?.split(' ').pop()?.toLowerCase() ?? ''} variants of this size`
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className="px-3 py-2 text-center">
                                      <Badge
                                        variant={v.is_active ? 'outline' : 'outline'}
                                        className="cursor-pointer"
                                        onClick={() =>
                                          update(
                                            v,
                                            { is_active: v.is_active ? 0 : 1 },
                                            v.is_active
                                              ? 'Variant deactivated'
                                              : 'Variant activated'
                                          )
                                        }
                                      >
                                        {v.is_active ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="px-3 py-2 text-center">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 text-muted-foreground hover:text-destructive"
                                        aria-label={`Deactivate ${v.variant}`}
                                        onClick={() => setPendingDelete(v)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination, mirroring DataTablePagination's layout */}
      <div className="flex w-full flex-col-reverse items-center justify-between gap-4 border-t border-border px-4 py-3 sm:flex-row sm:gap-8 md:px-5">
        <div className="flex-1 whitespace-nowrap text-muted-foreground text-sm">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
          <div className="flex items-center space-x-2">
            <p className="whitespace-nowrap font-medium text-sm">
              Products per page
            </p>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => onPageSizeChange(Number(val))}
            >
              <SelectTrigger className="h-8 w-[4.5rem] [&[data-size]]:h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 25, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              aria-label="Go to first page"
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={() => onPageChange(1)}
              disabled={currentPage <= 1 || isLoading}
            >
              <ChevronsLeft />
            </Button>
            <Button
              aria-label="Go to previous page"
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1 || isLoading}
            >
              <ChevronLeft />
            </Button>
            <Button
              aria-label="Go to next page"
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || isLoading}
            >
              <ChevronRight />
            </Button>
            <Button
              aria-label="Go to last page"
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage >= totalPages || isLoading}
            >
              <ChevronsRight />
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Deactivate {pendingDelete?.variant}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              It disappears from the customer catalog but stays in the database,
              so existing orders and share links keep working. You can reactivate
              it from the Status column.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = pendingDelete
                setPendingDelete(null)
                if (!target) return
                await onDeleteVariant(target.stock_id, false)
                toast.success(`${target.variant} deactivated`)
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

export default CatalogManagementTable
